import {
    DATE_TIME_FORMAT_NAME_MODEL,
    getDateFormat,
    getTimeFormat
} from "../../../../../../../Apis/functions";
import {IconRightArrow} from "../../../../../../../components/buttons/IconButtons";
import {useTranslation} from "react-i18next";
import {EMPTY_DEFAULT_DATE} from "../Model/Models";

/**
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle: Full Stack Developer - IBL
 * @Email: kpozos@ibl.mx
 *
 * AttendanceCycleApp
 *
 * Handles logic and helpers for rendering and validating attendance cycle configuration,
 * including supervisor and admin review ranges, date formatting, and UI helpers.
 *
 * @param {Object} props - Component properties
 * @param {Object} props.item - Attendance cycle configuration object
 * @param {Function} props.setItem - Function to update the item state
 * @param {Function} props.onChange - Change handler for input updates
 *
 * @returns {Object} Helpers and computed values for UI rendering
 * @returns {Function} returns.t - Translation function
 * @returns {boolean} returns.disabled - Indicates if attendance cycle is disabled
 * @returns {string} returns.supervisorIsValidTextFrom - Validation message for supervisor "from" range
 * @returns {string} returns.supervisorIsValidTextTo - Validation message for supervisor "to" range
 * @returns {string} returns.adminIsValidTextFrom - Validation message for admin "from" range
 * @returns {string} returns.adminIsValidTextTo - Validation message for admin "to" range
 * @returns {string} returns.legendBackground - CSS class for legend background
 * @returns {string} returns.legendFont - CSS class for legend font color
 * @returns {Function} returns.renderDate - Function to format date display
 * @returns {Function} returns.renderEndTime - Function to format time display
 * @returns {Function} returns.RangeRow - UI helper component to render a date range row
 * @returns {Function} returns.handleChange - Input change handler
 */
const AttendanceCycleApp = ({ item, setItem, onChange }) => {
    const { t } = useTranslation();
    const disabled = !item.enableAttendanceCycle;
    const legendBackground = disabled ? 'chip-shades-200-50': 'chip-primary-200-50';
    const legendFont = disabled ? 'font-color-shades-500': 'font-color-primary-700';
    const legendIcon = disabled ? 'icon-color-shades-500': 'icon-color-primary-700';

    /**
     * Generates validation message for "from" range fields.
     *
     * Rules:
     * - Skips validation if the attendance cycle is disabled
     * - Requires frequency value to be present
     * - Ensures the range does not exceed the attendance cycle duration
     * - Ensures value is greater than 0 (fallback message)
     *
     * @param {boolean} disabled - Whether the attendance cycle is disabled
     * @param {boolean} isValid - Indicates if the current range value is valid
     * @param {number} periodDays - Total duration of the attendance cycle in days
     * @param {number|string} frequencyValue - Frequency configuration value
     * @returns {string} Localized validation message or empty string if valid
     */
    const getRangeError = (disabled, isValid, periodDays, frequencyValue) => {
        if (disabled) return '';
        if (!frequencyValue) return t("x_is_required", { name: t('frequency_value') });
        if (!isValid) return t("must_be_less_than_or_equal_to_attendance_cycle", { value: periodDays });
        return 'this_value_must_be_greater_than_0';
    };

    /**
     * Generates validation message for "to" range fields.
     *
     * Rules:
     * - Skips validation if the attendance cycle is disabled
     * - Requires frequency value
     * - Requires "from" value to be defined
     * - Ensures the range does not exceed the attendance cycle duration
     * - Ensures "to" value does not exceed "from" value
     *
     * @param {boolean} disabled - Whether the attendance cycle is disabled
     * @param {number|string} fromValue - Starting value of the range
     * @param {boolean} isValid - Indicates if the current range value is valid
     * @param {number} periodDays - Total duration of the attendance cycle in days
     * @param {number|string} frequencyValue - Frequency configuration value
     * @returns {string} Localized validation message or empty string if valid
     */
    const getToError = (disabled, fromValue, isValid, periodDays, frequencyValue) => {
        if (disabled) return '';
        if (!frequencyValue) return t("x_is_required", { name: t('frequency_value') });
        if (!fromValue) return t("x_is_required", { name: t('days_before_period_starts') });
        if (!isValid) return t("must_be_less_than_or_equal_to_attendance_cycle", { value: periodDays });
        return t("must_be_less_than_or_equal_to_x", { value: fromValue });
    };

    const supervisorIsValidTextFrom = getRangeError(
        disabled,
        item.isValidSupervisorRangeFrom,
        item.periodDays,
        item.frequencyValue
    );

    const supervisorIsValidTextTo = getToError(
        disabled,
        item.supervisorReviewPeriodFrom,
        item.isValidSupervisorRangeTo,
        item.periodDays,
        item.frequencyValue
    );

    const adminIsValidTextFrom = getRangeError(
        disabled,
        item.isValidAdminRangeFrom,
        item.periodDays,
        item.frequencyValue
    );

    const adminIsValidTextTo = getToError(
        disabled,
        item.adminApprovalPeriodFrom,
        item.isValidAdminRangeTo,
        item.periodDays,
        item.frequencyValue
    );

    /**
     * Formats a date value based on attendance cycle state and validity.
     *
     * Behavior:
     * - Returns default format if attendance cycle is disabled
     * - Returns formatted date if valid
     * - Returns default fallback if invalid
     * - Uses a shorter format when default equals EMPTY_DEFAULT_DATE
     *
     * @param {boolean} isValid - Indicates if the date is valid
     * @param {Date|string|number} date - Date value to format
     * @param {string} defaultFormat - Fallback value when invalid or disabled
     * @returns {string} Formatted date or fallback value
     */
    const renderDate = (isValid, date, defaultFormat) => {
        if(!item.enableAttendanceCycle) return defaultFormat;

        return isValid
            ? getDateFormat(date, null, defaultFormat === EMPTY_DEFAULT_DATE ?
                DATE_TIME_FORMAT_NAME_MODEL.shortMonth :
                DATE_TIME_FORMAT_NAME_MODEL.shortMonthDayYearTime24WithSeconds)
            : defaultFormat;
    }

    /**
     * Formats time (end time) based on attendance cycle state and validity.
     *
     * Behavior:
     * - Returns default format if attendance cycle is disabled
     * - Returns formatted time if valid
     * - Returns fallback value if invalid
     *
     * @param {boolean} isValid - Indicates if the time is valid
     * @param {Date|string|number} date - Date value to extract time from
     * @param {string} defaultFormat - Fallback value when invalid or disabled
     * @returns {string} Formatted time or fallback value
     */
    const renderEndTime = (isValid, date, defaultFormat) => {
        if(!item.enableAttendanceCycle) return defaultFormat;

        return isValid
            ? getTimeFormat(date, DATE_TIME_FORMAT_NAME_MODEL.HHmmss) : defaultFormat;
    }

    /**
     * Arrow separator component used between date/time ranges.
     *
     * Provides consistent spacing and styling for visual flow.
     *
     * @returns {JSX.Element} Styled arrow icon element
     */
    const Arrow = () => (
        <span className="mx-1 d-inline-flex align-items-center" aria-hidden="true">
            <IconRightArrow className={`w-20px h-20px ${legendIcon}`} />
        </span>
    );

    /**
     * Renders a labeled range row (start → end).
     *
     * Used to display formatted date/time ranges with a label.
     *
     * @param {Object} props - Component props
     * @param {string} props.label - Label describing the range
     * @param {string} props.start - Formatted start value
     * @param {string} props.end - Formatted end value
     * @returns {JSX.Element} Rendered range row UI
     */
    const RangeRow = ({ label, start, end }) => (
        <div className="d-flex flex-wrap-no-gap align-items-center justify-content-start">
            <span className="pr-2">{label}</span>

            <span className={`py-1 fs-12px ibl-font-w-700 ${legendFont}`}>
                {start}
            </span>
            <Arrow />
            <span className={`py-1 fs-12px ibl-font-w-700 ${legendFont}`}>
                {end}
            </span>
        </div>
    );

    /**
     * Handles changes in attendance cycle inputs.
     *
     * Behavior:
     * - If the input value is empty, resets the item using its clear() method
     * - Otherwise, delegates the change event to the external handler
     *
     * @param {React.ChangeEvent<HTMLInputElement>} e - Input change event
     */
    const handleChange = (e) => {
        if(!e.target.value)
            setItem(item.clear());
        else{
            onChange(e);
        }
    }

    return {
        t,
        disabled,
        supervisorIsValidTextFrom,
        supervisorIsValidTextTo,
        adminIsValidTextFrom,
        adminIsValidTextTo,
        legendBackground,
        legendFont,
        renderDate,
        renderEndTime,
        RangeRow,
        handleChange
    }
}

export default AttendanceCycleApp;
